import { useState, useEffect, useMemo } from "react";
import { getBlogPosts, type BlogPost } from "../utils/adminStore";
import { injectStructuredData, getBlogPostSchema } from "../utils/seo";

interface BlogPostPageProps {
  slug: string;
}

// Inline Markdown Parser for bold, italic, inline-code, and links
function parseInlineMarkdown(text: string): React.ReactNode[] {
  const regex = /(\*\*.*?\*\*|\*.*?\*|`.*?`|\[.*?\]\(.*?\))/g;
  const parts = text.split(regex);

  return parts.map((part, i) => {
    if (!part) return null;
    if (part.startsWith("**") && part.endsWith("**") && part.length >= 4) {
      return (
        <strong key={i} className="font-bold text-slate-900 dark:text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith("*") && part.endsWith("*") && part.length >= 2) {
      return (
        <em key={i} className="italic text-slate-800 dark:text-slate-200">
          {part.slice(1, -1)}
        </em>
      );
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length >= 2) {
      return (
        <code
          key={i}
          className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-brand-dark dark:text-sky-300 font-mono text-xs font-semibold"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    const linkMatch = part.match(/^\[(.*?)\]\((.*?)\)$/);
    if (linkMatch) {
      const isExternal = linkMatch[2]?.startsWith("http");
      return (
        <a
          key={i}
          href={linkMatch[2]}
          className="text-brand dark:text-primary underline font-medium hover:opacity-80 transition-opacity"
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
        >
          {linkMatch[1]}
        </a>
      );
    }
    return part;
  });
}

// Code Block with Copy Action
function CodeBlock({ code, lang }: { code: string; lang: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-6 rounded-2xl overflow-hidden border border-slate-800/90 bg-[#080d16] shadow-xl">
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#0f1724] border-b border-slate-800 text-xs font-mono text-slate-400">
        <span className="text-primary font-bold uppercase tracking-wider">{lang || "CODE"}</span>
        <button
          onClick={handleCopy}
          className="px-2.5 py-1 rounded bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white transition-colors cursor-pointer text-[11px]"
        >
          {copied ? "✓ Copied" : "Copy Code"}
        </button>
      </div>
      <div className="p-4 sm:p-5 overflow-x-auto">
        <pre className="!bg-transparent !p-0 !m-0 !border-none font-mono text-xs text-sky-300 leading-relaxed">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  );
}

// Markdown Table Parser
function MarkdownTable({ lines }: { lines: string[] }) {
  if (lines.length < 2) return null;
  const headerCells = lines[0]!.split("|").map((c) => c.trim()).filter(Boolean);
  const rowLines = lines.slice(2); // skip header separator line

  return (
    <div className="my-6 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs">
      <table className="w-full text-left text-xs sm:text-sm">
        <thead className="bg-slate-100 dark:bg-slate-800/80 text-brand-dark dark:text-slate-200 border-b border-slate-200 dark:border-slate-700">
          <tr>
            {headerCells.map((h, i) => (
              <th key={i} className="px-4 py-3 font-bold uppercase tracking-wider text-[11px]">
                {parseInlineMarkdown(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-800 text-slate-700 dark:text-slate-300">
          {rowLines.map((row, rIdx) => {
            const cells = row.split("|").map((c) => c.trim()).filter(Boolean);
            return (
              <tr key={rIdx} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                {cells.map((cell, cIdx) => (
                  <td key={cIdx} className="px-4 py-3">
                    {parseInlineMarkdown(cell)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function BlogPostPage({ slug }: BlogPostPageProps) {
  const [copied, setCopied] = useState(false);
  const posts = getBlogPosts();
  const post = posts.find((p) => p.slug === slug);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | RADIECE Engineering Blog`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute("content", post.excerpt);

      injectStructuredData(
        getBlogPostSchema({
          title: post.title,
          excerpt: post.excerpt,
          slug: post.slug,
          publishedAt: post.publishedAt,
          author: post.author,
          tags: post.tags,
        })
      );
    }
  }, [post]);

  const relatedPosts = useMemo(() => {
    if (!post) return [];
    return posts
      .filter((p) => p.slug !== post.slug && (p.category === post.category || p.tags.some((t) => post.tags.includes(t))))
      .slice(0, 3);
  }, [post, posts]);

  const handleCopyLink = () => {
    if (typeof window !== "undefined") {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!post) {
    return (
      <div className="py-24 text-center px-4">
        <div className="max-w-md mx-auto bg-white dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 p-8 rounded-2xl">
          <h1 className="text-2xl font-bold text-brand-dark dark:text-slate-100 mb-2">
            Article Not Found
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
            The requested technical publication could not be located.
          </p>
          <a
            href="/blog"
            className="inline-block px-5 py-2.5 bg-brand dark:bg-primary text-white dark:text-slate-950 font-bold rounded-xl text-xs uppercase tracking-wider"
          >
            ← Return to Blog Directory
          </a>
        </div>
      </div>
    );
  }

  // Comprehensive Block & Inline Markdown Parser
  const renderMarkdownBlocks = (content: string) => {
    const rawLines = content.trim().split("\n");
    const blocks: React.ReactNode[] = [];

    let i = 0;
    while (i < rawLines.length) {
      const line = rawLines[i]!;

      // 1. Fenced Code Block
      if (line.startsWith("```")) {
        const lang = line.slice(3).trim();
        const codeLines: string[] = [];
        i++;
        while (i < rawLines.length && !rawLines[i]!.startsWith("```")) {
          codeLines.push(rawLines[i]!);
          i++;
        }
        i++; // skip closing ```
        blocks.push(
          <CodeBlock
            key={`code-${i}`}
            code={codeLines.join("\n")}
            lang={lang}
          />
        );
        continue;
      }

      // 2. Markdown Table
      if (line.trim().startsWith("|") && line.trim().endsWith("|")) {
        const tableLines: string[] = [];
        while (i < rawLines.length && rawLines[i]!.trim().startsWith("|") && rawLines[i]!.trim().endsWith("|")) {
          tableLines.push(rawLines[i]!.trim());
          i++;
        }
        blocks.push(<MarkdownTable key={`table-${i}`} lines={tableLines} />);
        continue;
      }

      // 3. Headings
      if (line.startsWith("## ")) {
        const text = line.replace("## ", "");
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        blocks.push(
          <h2
            key={`h2-${i}`}
            id={id}
            className="text-xl sm:text-2xl font-bold text-brand-dark dark:text-slate-100 mt-10 mb-4 tracking-tight border-b border-slate-200 dark:border-slate-800 pb-2"
          >
            {parseInlineMarkdown(text)}
          </h2>
        );
        i++;
        continue;
      }

      if (line.startsWith("### ")) {
        const text = line.replace("### ", "");
        blocks.push(
          <h3
            key={`h3-${i}`}
            className="text-lg font-bold text-brand-dark dark:text-slate-100 mt-6 mb-3"
          >
            {parseInlineMarkdown(text)}
          </h3>
        );
        i++;
        continue;
      }

      // 4. Horizontal Rule
      if (line.startsWith("---")) {
        blocks.push(<hr key={`hr-${i}`} className="my-8 border-slate-200 dark:border-slate-800" />);
        i++;
        continue;
      }

      // 5. Checklist Items
      if (line.startsWith("- [x] ") || line.startsWith("- [ ] ")) {
        const checked = line.startsWith("- [x] ");
        const text = line.replace(/- \[[ x]\] /, "");
        blocks.push(
          <div key={`chk-${i}`} className="flex items-center gap-2.5 text-sm text-slate-700 dark:text-slate-300 my-2 font-medium">
            <span
              className={`w-5 h-5 rounded-md flex items-center justify-center text-xs font-bold ${
                checked
                  ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                  : "bg-slate-200 dark:bg-slate-800 text-slate-400 border border-slate-300 dark:border-slate-700"
              }`}
            >
              {checked ? "✓" : ""}
            </span>
            <span>{parseInlineMarkdown(text)}</span>
          </div>
        );
        i++;
        continue;
      }

      // 6. Unordered Lists (group contiguous bullet points)
      if (line.startsWith("- ") || line.startsWith("* ")) {
        const listItems: string[] = [];
        while (i < rawLines.length && (rawLines[i]!.startsWith("- ") || rawLines[i]!.startsWith("* "))) {
          listItems.push(rawLines[i]!.replace(/^[-*]\s+/, ""));
          i++;
        }
        blocks.push(
          <ul key={`ul-${i}`} className="my-4 space-y-2 list-disc pl-5 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed font-light">
            {listItems.map((item, idx) => (
              <li key={idx}>
                {parseInlineMarkdown(item)}
              </li>
            ))}
          </ul>
        );
        continue;
      }

      // 7. Ordered Lists (group contiguous numbered items)
      if (/^\d+\.\s+/.test(line)) {
        const listItems: string[] = [];
        while (i < rawLines.length && /^\d+\.\s+/.test(rawLines[i]!)) {
          listItems.push(rawLines[i]!.replace(/^\d+\.\s+/, ""));
          i++;
        }
        blocks.push(
          <ol key={`ol-${i}`} className="my-4 space-y-2.5 list-decimal pl-5 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed font-light">
            {listItems.map((item, idx) => (
              <li key={idx} className="pl-1">
                {parseInlineMarkdown(item)}
              </li>
            ))}
          </ol>
        );
        continue;
      }

      // 8. Blockquote
      if (line.startsWith("> ")) {
        const text = line.replace(/^>\s+/, "");
        blocks.push(
          <blockquote key={`quote-${i}`} className="my-4 p-4 border-l-4 border-primary bg-slate-50 dark:bg-[#101c2a] rounded-r-xl text-sm italic text-slate-700 dark:text-slate-300">
            {parseInlineMarkdown(text)}
          </blockquote>
        );
        i++;
        continue;
      }

      // 9. Standard Paragraph
      if (line.trim().length > 0) {
        blocks.push(
          <p key={`p-${i}`} className="text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed font-light my-4">
            {parseInlineMarkdown(line)}
          </p>
        );
      }

      i++;
    }

    return blocks;
  };

  return (
    <article className="bg-white dark:bg-[#090e17] py-12 sm:py-16 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <div className="mb-6 flex items-center gap-2 text-xs text-slate-400 font-mono">
          <a href="/" className="hover:text-brand dark:hover:text-primary">Home</a>
          <span>/</span>
          <a href="/blog" className="hover:text-brand dark:hover:text-primary">Technical Blog</a>
          <span>/</span>
          <span className="truncate max-w-xs text-slate-600 dark:text-slate-300">{post.title}</span>
        </div>

        {/* Article Header */}
        <header className="max-w-4xl mb-10">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-primary/10 border border-primary/20 text-brand dark:text-primary text-xs font-bold rounded-full uppercase tracking-wider">
              {post.category}
            </span>
            <span className="text-xs font-mono text-slate-400">
              Published on {post.publishedAt}
            </span>
            <span className="text-xs font-mono text-slate-400">• {post.readingTime}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight leading-tight mb-6">
            {post.title}
          </h1>

          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 font-light leading-relaxed border-l-4 border-primary pl-4 py-1">
            {post.excerpt}
          </p>
        </header>

        {/* 2-Column Reader Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-12 items-start">
          {/* Main Article Body */}
          <main className="max-w-none">
            {renderMarkdownBlocks(post.content)}

            {/* Tags Strip */}
            <div className="mt-10 pt-6 border-t border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-slate-400 uppercase mr-2">Tags:</span>
              {post.tags.map((t) => (
                <span
                  key={t}
                  className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-xs font-mono text-slate-600 dark:text-slate-300 rounded-md"
                >
                  #{t}
                </span>
              ))}
            </div>
          </main>

          {/* Sticky Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-24">
            {/* Author Profile Card */}
            <div className="bg-slate-50 dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                Author & Practice
              </h3>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 text-brand dark:text-primary flex items-center justify-center font-bold text-sm">
                  {post.author.name.charAt(0)}
                </div>
                <div>
                  <h4 className="text-sm font-bold text-brand-dark dark:text-slate-100">{post.author.name}</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{post.author.role}</p>
                </div>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-light">
                Engineering team at RADIECE Private Limited, specializing in enterprise systems and embedded hardware.
              </p>
            </div>

            {/* Share & Copy Link */}
            <div className="bg-slate-50 dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                Share Publication
              </h3>
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleCopyLink}
                  className="w-full py-2.5 px-4 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-700 dark:text-slate-200 hover:border-brand dark:hover:border-primary transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>🔗</span> {copied ? "Link Copied!" : "Copy Article Link"}
                </button>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(`https://www.radiece.com/blog/${post.slug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2.5 px-4 rounded-xl bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20 text-xs font-bold text-center hover:bg-sky-500/20 transition-colors"
                >
                  Share on X (Twitter) →
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://www.radiece.com/blog/${post.slug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2.5 px-4 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 text-xs font-bold text-center hover:bg-blue-500/20 transition-colors"
                >
                  Share on LinkedIn →
                </a>
              </div>
            </div>

            {/* Related Articles */}
            {relatedPosts.length > 0 && (
              <div className="bg-slate-50 dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">
                  Related Publications
                </h3>
                <div className="space-y-3">
                  {relatedPosts.map((r) => (
                    <a
                      key={r.slug}
                      href={`/blog/${r.slug}`}
                      className="block p-3 rounded-xl bg-white dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 hover:border-primary transition-colors group"
                    >
                      <span className="text-[10px] font-bold text-primary uppercase">{r.category}</span>
                      <h4 className="text-xs font-bold text-brand-dark dark:text-slate-100 group-hover:text-primary transition-colors mt-0.5 line-clamp-2">
                        {r.title}
                      </h4>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </article>
  );
}
